<template>
  <v-container fluid class="analysis-queue-page">
    <div class="analysis-queue-page__header">
      <div class="analysis-queue-page__title">
        <v-icon left color="primary">mdi-tray-full</v-icon>
        <span class="headline">Analysis queue</span>
        <v-chip
          small
          :color="paused ? 'warning' : 'success'"
          dark
          class="ml-3 analysis-queue-page__state"
        >
          {{ paused ? 'Queue paused' : 'Queue running' }}
        </v-chip>
        <v-spacer />
        <v-btn
          small
          outlined
          :color="paused ? 'success' : 'warning'"
          class="analysis-queue-page__pause-toggle"
          :loading="pauseBusy"
          :disabled="pauseBusy"
          @click="togglePause"
        >
          <v-icon small left>{{ paused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
          {{ paused ? 'Resume queue' : 'Pause queue' }}
        </v-btn>
      </div>
      <div class="caption grey--text text--darken-1 analysis-queue-page__subtitle">
        Waiting, running and recent analysis runs. Updates arrive over the socket and every
        {{ pollSeconds }} s while this tab is visible.
      </div>
    </div>

    <QueueHealthStrip
      :slot-usage="slots"
      :health="health"
      :versions="versions"
      :live="live"
      :loading="loading"
      :last-updated-at="lastUpdatedAt"
      :now="now"
      @refresh="refresh"
    />

    <v-alert v-if="error && !hasAnyRun" type="error" outlined class="analysis-queue-page__error">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" class="analysis-queue-page__retry" @click="refresh">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <v-row v-else class="analysis-queue-page__lanes">
      <v-col cols="12" md="4">
        <QueueLane
          lane-key="waiting"
          title="Waiting"
          icon="mdi-timer-sand"
          :runs="waiting"
          :loading="loading"
          empty-text="Queue is empty"
          draggable
          :drag-disabled="reorderBusy"
          @reorder="onReorder"
        >
          <template #toolbar>
            <span class="caption grey--text analysis-queue-page__drag-hint">Drag to reorder</span>
          </template>
          <template #default="{ run, index }">
            <QueueRunCard
              :run="run"
              :now="now"
              :pending="isPending(run._id)"
              :queue-index="index + 1"
              @remove="onRemove"
              @open="openRun"
            />
          </template>
        </QueueLane>
      </v-col>

      <v-col cols="12" md="4">
        <QueueLane
          lane-key="running"
          title="Running"
          icon="mdi-cog-play"
          :runs="running"
          :loading="loading"
          empty-text="Queue is empty"
        >
          <template #default="{ run }">
            <QueueRunCard
              :run="run"
              :now="now"
              :pending="isPending(run._id)"
              :force-enabled="forceEnabled"
              @cancel="askCancel"
              @force="askForce"
              @mark-failed="askMarkFailed"
              @open="openRun"
            />
          </template>
        </QueueLane>
      </v-col>

      <v-col cols="12" md="4">
        <QueueLane
          lane-key="recent"
          title="Recent"
          icon="mdi-history"
          :runs="filteredRecent"
          :loading="loading"
          empty-icon="mdi-check-all"
          :empty-text="recentEmptyText"
        >
          <template #toolbar>
            <v-btn-toggle
              v-model="recentFilter"
              dense
              mandatory
              group
              class="analysis-queue-page__recent-filter"
            >
              <v-btn x-small value="all">All</v-btn>
              <v-btn x-small value="SUCCEEDED">Succeeded</v-btn>
              <v-btn x-small value="FAILED">Failed</v-btn>
              <v-btn x-small value="CANCELLED">Cancelled</v-btn>
            </v-btn-toggle>
          </template>
          <template #default="{ run }">
            <QueueRunCard :run="run" :now="now" :pending="isPending(run._id)" @open="openRun" />
          </template>
        </QueueLane>
      </v-col>
    </v-row>

    <div class="analysis-queue-page__footer caption">
      <router-link to="/manual-video-analysis">Open manual video analysis</router-link>
      <span class="mx-2 grey--text">·</span>
      <router-link to="/candidates">Open candidates</router-link>
    </div>

    <!-- Cancel (running) confirmation -->
    <v-dialog v-model="cancelDialog.open" max-width="440">
      <v-card class="analysis-queue-page__cancel-dialog">
        <v-card-title class="py-3">Cancel {{ runLabel(cancelDialog.run) }}?</v-card-title>
        <v-card-text>
          The worker stops at its next checkpoint and partial results are discarded. The source
          returns to its previous status.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDialog.open = false">Keep running</v-btn>
          <v-btn
            color="warning"
            depressed
            class="analysis-queue-page__cancel-confirm"
            @click="confirmCancel"
          >
            Cancel run
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mark as failed confirmation -->
    <v-dialog v-model="markFailedDialog.open" max-width="480">
      <v-card class="analysis-queue-page__mark-failed-dialog">
        <v-card-title class="py-3"
          >Mark {{ runLabel(markFailedDialog.run) }} as failed?</v-card-title
        >
        <v-card-text>
          <v-alert type="warning" dense outlined class="mb-2">
            Use this only when the worker stopped heartbeating. The run ends as <em>Failed</em>
            (worker lost) and the source returns to its previous status. If the worker is still
            alive the API refuses with 409.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="markFailedDialog.open = false">Keep waiting</v-btn>
          <v-btn
            color="error"
            depressed
            class="analysis-queue-page__mark-failed-confirm"
            @click="confirmMarkFailed"
          >
            Mark as failed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ForceCancelDialog
      v-model="forceDialog.open"
      :run="forceDialog.run"
      :busy="forceDialog.busy"
      @confirm="confirmForce"
    />

    <v-snackbar v-model="snackbar.open" :timeout="6000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.open = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import ForceCancelDialog from '@/components/queue/ForceCancelDialog.vue';
import { describeActionError, sourceLabel, sourceRoute } from '@/components/queue/queueFormat';
import QueueHealthStrip from '@/components/queue/QueueHealthStrip.vue';
import QueueLane from '@/components/queue/QueueLane.vue';
import QueueRunCard from '@/components/queue/QueueRunCard.vue';
import { POLL_INTERVAL_MS } from '@/store/modules/analysisQueue';

const AGE_TICK_MS = 15000;

/**
 * Live analysis queue (M1, task F12): three lanes fed by the `analysisQueue` store module, a
 * health strip and the run control actions (Delete / Cancel / Force cancel / Mark as failed).
 * M3 (F12b): drag and drop reordering of the Waiting lane (PATCH position, optimistic with
 * rollback) and the Pause / Resume queue button.
 */
export default {
  name: 'AnalysisQueue',

  components: {
    ForceCancelDialog,
    QueueHealthStrip,
    QueueLane,
    QueueRunCard,
  },

  data() {
    return {
      now: Date.now(),
      ageTimer: null,
      recentFilter: 'all',
      cancelDialog: { open: false, run: null },
      markFailedDialog: { open: false, run: null },
      forceDialog: { open: false, run: null, busy: false },
      snackbar: { open: false, text: '', color: 'error' },
      pauseBusy: false,
      reorderBusy: false,
    };
  },

  computed: {
    ...mapState('analysisQueue', [
      'waiting',
      'running',
      'recent',
      'slots',
      'health',
      'versions',
      'paused',
      'live',
      'lastUpdatedAt',
      'loading',
      'error',
    ]),
    ...mapGetters('analysisQueue', ['isPending']),
    pollSeconds() {
      return Math.round(POLL_INTERVAL_MS / 1000);
    },
    hasAnyRun() {
      return this.waiting.length + this.running.length + this.recent.length > 0;
    },
    filteredRecent() {
      if (this.recentFilter === 'all') return this.recent;
      return this.recent.filter(
        (run) => String(run.status || '').toUpperCase() === this.recentFilter
      );
    },
    recentEmptyText() {
      return this.recentFilter === 'all'
        ? 'Nothing finished in the last 24 h'
        : `No ${this.recentFilter.toLowerCase()} runs in the last 24 h`;
    },
    /** The read model may expose the server flag; until then the button is shown and 403 is explained. */
    forceEnabled() {
      return this.health?.force_cancel_enabled !== false;
    },
  },

  mounted() {
    this.startLive();
    this.ageTimer = setInterval(() => {
      this.now = Date.now();
    }, AGE_TICK_MS);
  },

  beforeDestroy() {
    this.stopLive();
    if (this.ageTimer) clearInterval(this.ageTimer);
  },

  methods: {
    ...mapActions('analysisQueue', [
      'startLive',
      'stopLive',
      'fetchQueue',
      'cancel',
      'remove',
      'force',
      'markFailed',
      'reorder',
      'pause',
      'resume',
    ]),

    runLabel(run) {
      if (!run) return 'run';
      const attempt = run.attempt != null ? `run ${run.attempt} of ` : '';
      return `${attempt}${sourceLabel(run)}`;
    },

    async refresh() {
      try {
        await this.fetchQueue();
        this.now = Date.now();
      } catch (_error) {
        // Shown through state.error / the alert.
      }
    },

    openRun(run) {
      const route = sourceRoute(run);
      if (!route) return;
      this.$router.push(route).catch(() => {});
    },

    notify(text, color = 'error') {
      this.snackbar = { open: true, text, color };
    },

    async runAction(action, label, request) {
      try {
        await request();
        this.notify(label, 'success');
        return true;
      } catch (error) {
        this.notify(describeActionError(error, action));
        return false;
      }
    },

    /** Drop in the Waiting lane: optimistic move in the store, PATCH position, rollback on error. */
    async onReorder({ runId, newIndex, run }) {
      if (!runId || this.reorderBusy) return;
      this.reorderBusy = true;
      try {
        const changed = await this.reorder({ runId, newIndex });
        if (changed !== false)
          this.notify(`Moved ${this.runLabel(run)} to #${newIndex + 1}`, 'success');
      } catch (error) {
        this.notify(describeActionError(error, 'reorder'));
      } finally {
        this.reorderBusy = false;
      }
    },

    async togglePause() {
      if (this.pauseBusy) return;
      this.pauseBusy = true;
      const pausing = !this.paused;
      try {
        await (pausing ? this.pause() : this.resume());
        this.notify(
          pausing ? 'Queue paused: nothing new is dispatched' : 'Queue resumed',
          'success'
        );
      } catch (error) {
        this.notify(describeActionError(error, pausing ? 'pause' : 'resume'));
      } finally {
        this.pauseBusy = false;
      }
    },

    /** Delete a waiting run: no confirmation, it has not started. */
    onRemove(run) {
      return this.runAction('remove', `Deleted ${this.runLabel(run)}`, () => this.remove(run._id));
    },

    askCancel(run) {
      this.cancelDialog = { open: true, run };
    },
    async confirmCancel() {
      const run = this.cancelDialog.run;
      this.cancelDialog = { open: false, run: null };
      if (!run) return;
      await this.runAction('cancel', `Cancel requested for ${this.runLabel(run)}`, () =>
        this.cancel(run._id)
      );
    },

    askMarkFailed(run) {
      this.markFailedDialog = { open: true, run };
    },
    async confirmMarkFailed() {
      const run = this.markFailedDialog.run;
      this.markFailedDialog = { open: false, run: null };
      if (!run) return;
      await this.runAction('mark-failed', `${this.runLabel(run)} marked as failed`, () =>
        this.markFailed(run._id)
      );
    },

    askForce(run) {
      this.forceDialog = { open: true, run, busy: false };
    },
    async confirmForce({ run, confirm }) {
      if (!run) return;
      this.forceDialog.busy = true;
      const ok = await this.runAction('force', `Force cancel sent for ${this.runLabel(run)}`, () =>
        this.force({ runId: run._id, confirm })
      );
      this.forceDialog = { open: !ok, run: ok ? null : run, busy: false };
    },
  },
};
</script>

<style scoped>
.analysis-queue-page {
  padding-top: 72px;
  max-width: 1440px;
}
.analysis-queue-page__header {
  margin-bottom: 12px;
}
.analysis-queue-page__title {
  display: flex;
  align-items: center;
}
.analysis-queue-page__subtitle {
  margin-top: 2px;
}
.analysis-queue-page__lanes > .col {
  display: flex;
  flex-direction: column;
}
.analysis-queue-page__footer {
  margin-top: 16px;
}
.analysis-queue-page__recent-filter {
  flex-wrap: nowrap;
}
</style>
